import { useRequiredContext } from "@redotech/react-util/context";
import AiStarOrangeIcon from "@redotech/redo-icons/ai-star-orange.svg";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import CalendarIconFilled from "@redotech/redo-icons/calendar_filled.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import InventoryAdjustmentsIcon from "@redotech/redo-icons/inventory_adjustments.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import PricingRulesIcon from "@redotech/redo-icons/receipt-money.svg";
import PricingRulesFilledIcon from "@redotech/redo-icons/receipt-money_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import SuppliersIcon from "@redotech/redo-icons/suppliers.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import ReportIconFilled from "@redotech/redo-merchant-inventory-app/icons/report-icon-filled.svg";
import ReportIcon from "@redotech/redo-merchant-inventory-app/icons/report-icon.svg";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { CatalogProductsItem } from "./catalog-products";
import { ForecastingItem } from "./forecasting";
import { InventoryLevelsItem } from "./inventory-levels";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";
import { TransfersItem } from "./transfers";

export const InventoryItem = memo(function InventoryItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const location = useLocation();
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isRedoEmployee =
    user?.email?.endsWith("@redo.com") || user?.email?.endsWith("@getredo.com");

  const isCatalogAgentUser = isRedoEmployee;

  // Check if marketplace is enabled for this team
  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  // Check if forecasting is enabled for this team
  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;

  // Check if forecasting billing is enabled (for forecasting-only customers)
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  // Check if catalog is enabled for this team
  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;

  // Check if pricing rules is enabled for this team
  const isPricingRulesEnabled = team.settings.ims?.pricingRulesEnabled ?? false;

  const isRecommerceChildSelected =
    location.pathname.includes("/liquidation/") ||
    location.pathname.endsWith("/liquidation");

  // Check if any catalog child routes are selected
  const isCatalogChildSelected =
    location.pathname.includes("/catalog/") ||
    location.pathname.endsWith("/catalog");

  // Check if any inventory child routes are selected
  const isInventoryChildSelected =
    location.pathname.includes("/inventory/") ||
    location.pathname.endsWith("/inventory");

  // Check if suppliers routes are selected
  const isSuppliersSelected =
    location.pathname.includes("/suppliers") ||
    location.pathname.endsWith("/suppliers");

  // Check if document templates routes are selected
  const isDocumentTemplatesSelected =
    location.pathname.includes("/document-templates") ||
    location.pathname.endsWith("/document-templates");

  // Check if any supplier-related child routes are selected
  const isSuppliersChildSelected =
    isSuppliersSelected || isDocumentTemplatesSelected;

  // Check if bill of materials routes are selected
  const isBillOfMaterialsSelected =
    location.pathname.includes("/bill-of-materials") ||
    location.pathname.endsWith("/bill-of-materials");

  return (
    <>
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isCatalogChildSelected}
        FilledIcon={CatalogIconFilled}
        friendlyName="Catalog"
        hidden={!isCatalogEnabled}
        id="catalog"
        isFolder
        UnfilledIcon={CatalogIcon}
      >
        <CatalogProductsItem />
        {/* Only available to the Redo Outlet team */}
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/catalog/consignment`,
          }}
          friendlyName="Consignment"
          hidden={!(team._id === "68800d96229f8fc10f4dc1ce")}
          id="catalog-consignment"
        />
      </RedoSecondaryNavItem>

      {imsEnabled ? (
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isInventoryChildSelected}
          FilledIcon={PackageIconFilled}
          friendlyName="Inventory"
          id="inventory"
          isFolder
          UnfilledIcon={PackageIcon}
        >
          <InventoryLevelsItem />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/pricing-rules`,
            }}
            FilledIcon={PricingRulesFilledIcon}
            friendlyName="Pricing Rules"
            hidden={!isPricingRulesEnabled}
            id="pricing-rules"
            UnfilledIcon={PricingRulesIcon}
          />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/adjustments`,
            }}
            FilledIcon={InventoryAdjustmentsIcon}
            friendlyName="Inventory Adjustments"
            id="inventory-adjustments"
            UnfilledIcon={InventoryAdjustmentsIcon}
          />
          <ForecastingItem />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/scenario-planning`,
            }}
            FilledIcon={CalendarIconFilled}
            friendlyName="Scenario planning"
            hidden={!isRedoEmployee || !isForecastingEnabled}
            id="scenario-planning"
            UnfilledIcon={CalendarIcon}
          />
          <RedoSecondaryNavItem
            action={{ type: "button" }}
            anyChildSelected={isSuppliersChildSelected}
            FilledIcon={SuppliersIcon}
            friendlyName="Suppliers"
            hidden={!isForecastingEnabled}
            id="suppliers"
            isFolder
            UnfilledIcon={SuppliersIcon}
          >
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: `${routes.inventoryManagementBaseUrl}/suppliers`,
              }}
              friendlyName="All Suppliers"
              id="suppliers-all"
            />
            <RedoSecondaryNavItem
              action={{
                type: "link",
                href: `${routes.inventoryManagementBaseUrl}/document-templates`,
              }}
              friendlyName="PO Templates"
              id="document-templates"
            />
          </RedoSecondaryNavItem>
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/bill-of-materials`,
            }}
            anyChildSelected={isBillOfMaterialsSelected}
            FilledIcon={LayersIconFilled}
            friendlyName="Bill of Materials"
            id="bill-of-materials"
            UnfilledIcon={LayersIcon}
          />
          <PurchaseOrdersItem />
          <ShipmentsItem />
          <TransfersItem />
        </RedoSecondaryNavItem>
      ) : isForecastingEnabled || isForecastingBillingEnabled ? (
        // Forecasting-only customers (no IMS subscription)
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isInventoryChildSelected}
          FilledIcon={PackageIconFilled}
          friendlyName="Inventory"
          id="inventory"
          isFolder
          UnfilledIcon={PackageIcon}
        >
          <ForecastingItem />
          <RedoSecondaryNavItem
            action={{
              type: "link",
              href: `${routes.inventoryManagementBaseUrl}/onboarding`,
            }}
            friendlyName="Plans"
            id="inventory-plans"
          />
        </RedoSecondaryNavItem>
      ) : (
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/onboarding`,
          }}
          FilledIcon={PackageIconFilled}
          friendlyName="Inventory"
          id="inventory"
          UnfilledIcon={PackageIcon}
        />
      )}

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/reports`,
        }}
        FilledIcon={ReportIconFilled}
        friendlyName="Reporting"
        hidden={!imsEnabled}
        id="reporting"
        UnfilledIcon={ReportIcon}
      />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/catalog-agent`,
        }}
        FilledIcon={AiStarOrangeIcon}
        friendlyName="Intelligent Inventory"
        hidden={!isCatalogAgentUser}
        id="catalog-ims-agent"
        UnfilledIcon={AiStarIcon}
      />

      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isRecommerceChildSelected}
        FilledIcon={RefreshIcon}
        friendlyName="Recommerce"
        hidden={!isMarketplaceEnabled}
        id="recommerce"
        isFolder
        UnfilledIcon={RefreshIconOutline}
      >
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/liquidation/dashboard`,
          }}
          friendlyName="Dashboard"
          id="liquidation-dashboard"
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: `${routes.inventoryManagementBaseUrl}/liquidation/products`,
          }}
          friendlyName="All listings"
          id="liquidation-all-products"
        />
      </RedoSecondaryNavItem>
    </>
  );
});
